import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  Grid,
  Typography,
} from '@mui/material';
import Page from '../components/Page';
import { AppColors, config, typographyClamps } from '../utils/utils';
import SearchBar from '../components/SearchBar';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector } from '../store';
import {
  setStatus as setCarStatus,
  reset as resetCars,
  setLoading as setCarLoading,
} from '../store/cars/carsSlice';
import { findCar } from '../store/cars/api';
import Loader from '../components/Loader';
import { getAssetURL } from '../utils/utilMethods';
import { CarAvailability } from '../enum';
import InventoryFilterDrawer from './InventoryFilterDrawer';
import { parseCurrencyString } from '../utils/formateCarPrice';
import { useLocation, useNavigate } from 'react-router-dom';
import { encryptString } from '../utils/url';

export interface FilterInterface {
  carType?: string;
  priceOrder?: string;
  available?: string;
}

const carType = [
  { name: 'Sedan', image: '/static/inventory_page/sedan.png', code: 'sedan' },
  { name: 'Truck', image: '/static/inventory_page/truck.png', code: 'truck' },
  { name: 'Coupe', image: '/static/inventory_page/coupe.png', code: 'coupe' },
  {
    name: 'Minivan',
    image: '/static/inventory_page/minivan.png',
    code: 'minivan',
  },
  {
    name: 'Electric',
    image: '/static/inventory_page/electric.png',
    code: 'electric',
  },
  { name: 'SUV', image: '/static/inventory_page/suv.png', code: 'suv' },
  {
    name: 'Convertible',
    image: '/static/inventory_page/convertible.png',
    code: 'convertible',
  },
  {
    name: 'Crossover',
    image: '/static/inventory_page/crossover.png',
    code: 'crossover',
  },
];

const Inventory: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const inventorySectionRef = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [selectedBodyType, setSelectedBodyType] = useState('');
  const [showCarType, setShowCarType] = useState(true);
  const [hasRecordFound, setHasRecordFound] = useState(false);
  const [commonFilter, setCommonFilter] = useState<FilterInterface>({});
  const [toggleDrawerState, setToggleDrawerState] = useState({
    filter: false,
  });

  const { data, isLoading } = useAppSelector(state => state.cars);

  useEffect(() => {
    if (
      (location.state as any)?.scrollToInventoryHeader &&
      inventorySectionRef.current
    ) {
      inventorySectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [location.state]);

  const debouncedHandleSearch = useMemo(
    () =>
      debounce((value: string, filter: FilterInterface) => {
        dispatch(
          findCar({
            dealerId: config.DEALER_ID,
            ...(value && { search: value }),
            ...(filter.carType && { carType: filter.carType }),
            ...(filter.priceOrder && { priceOrder: filter.priceOrder }),
          })
        );
      }, 700),
    [dispatch]
  );

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    if (value.trim() === '') {
      debouncedHandleSearch.cancel();
      dispatch(resetCars());
      dispatch(setCarStatus({ status: 'idle' }));
      setSelectedBodyType('');
      setHasRecordFound(false);
      setShowCarType(true);
    } else {
      dispatch(resetCars());
      dispatch(setCarLoading({ loading: true }));
      setShowCarType(false);
      setSelectedBodyType('');
      await debouncedHandleSearch(value, commonFilter);
      setHasRecordFound(true);
    }
  };

  const handleCarTypeClick = async (code: string) => {
    if (selectedBodyType === code) {
      setSelectedBodyType('');
      setHasRecordFound(false);
      setShowCarType(true);
      dispatch(resetCars());
      dispatch(setCarStatus({ status: 'idle' }));
      return;
    }
    setSelectedBodyType(code);
    dispatch(resetCars());
    dispatch(setCarLoading({ loading: true }));

    setShowCarType(true);
    await dispatch(
      findCar({
        dealerId: config.DEALER_ID,
        ...(search && { search }),
        bodyType: code,
        ...(commonFilter.priceOrder && { priceOrder: commonFilter.priceOrder }),
      })
    );
    setHasRecordFound(true);
  };

  const toggleDrawer = (name: string, open: boolean) => {
    setToggleDrawerState(curr => ({ ...curr, [name]: open }));
  };

  const openFilterDrawer = () => {
    toggleDrawer('filter', true);
  };

  const handleResetFilters = async () => {
    await setCommonFilter({});
    setSelectedBodyType('');
    setShowCarType(true);
    dispatch(resetCars());
    dispatch(setCarStatus({ status: 'idle' }));
    toggleDrawer('filter', false);
    setHasRecordFound(false);
  };

  const isFilterApplied = () => {
    return !!commonFilter.carType || !!commonFilter.priceOrder;
  };

  const applyFilter = async () => {
    setSelectedBodyType('');

    dispatch(resetCars());
    await dispatch(
      findCar({
        dealerId: config.DEALER_ID,
        ...(search && { search }),
        ...(commonFilter.carType && { carType: commonFilter.carType }),
        ...(commonFilter.priceOrder !== '' && {
          priceOrder: commonFilter.priceOrder,
        }),
        // ...(selectedBodyType && { bodyType: selectedBodyType }),
      })
    );
    setHasRecordFound(true);
  };

  const handleScrollToInventory = () => {
    inventorySectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <>
      <Page title={'Inventory'}>
        <Box
          display={'flex'}
          justifyContent={'center'}
        >
          <Box
            component={'img'}
            src='/static/home_page/img_brand_logos.png'
            sx={{ width: { xs: '100%', sm: '70%' } }}
          />
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            backgroundImage: `url('/static/home_page/img_sterling_banner.png')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: '100%',
              flexDirection: 'column',
              alignItems: 'center',
              justifyItems: 'center',
              p: 1,
              display: { xs: 'flex' },
            }}
          >
            <Typography
              sx={{
                fontSize: typographyClamps.h2,
                fontFamily: 'Poppins',
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              Luxury Without Limits
            </Typography>
            <Button
              // variant='contained'
              sx={{
                borderRadius: 0,
                backgroundColor: AppColors.primaryColor,
                color: 'white',
                // width: '120px',
                textTransform: 'none',
                // p: 0.2,
                // px: 1,
                // mt: 1,
                padding: '2px 26px',
                fontSize: typographyClamps.h6,
                whiteSpace: 'pre-line',
                lineHeight: 1.35,
                '&:hover': {
                  borderRadius: 0,
                },
              }}
              onClick={handleScrollToInventory}
            >
              {'View\nInventory'}
            </Button>
          </Box>
        </Box>

        <Grid
          container
          ref={inventorySectionRef}
          sx={{
            scrollMarginTop: { xs: '80px', sm: '0px' },
            py: 1,
            px: { xs: 2, sm: 5 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Grid size={{ xs: 12, sm: 4, md: 6 }}>
            <Box
              display='flex'
              alignItems='center'
              gap={2}
              sx={{
                justifyContent: { xs: 'center', sm: 'start' },
              }}
            >
              <Box
                component='img'
                src='/static/inventory_page/inventory.png'
                alt='Inventory'
                sx={{ width: 60, height: 60, objectFit: 'contain' }}
              />

              <Box>
                <Typography
                  sx={{
                    fontSize: typographyClamps.h4,
                    fontWeight: 800,
                  }}
                >
                  Inventory
                </Typography>
                <Typography
                  sx={{
                    fontSize: typographyClamps.h6,
                    fontWeight: 600,
                    color: 'brown',
                  }}
                >
                  Shop by Style
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            <Box
              display='flex'
              alignItems='center'
              width='100%'
            >
              <Box flex={1}>
                <SearchBar
                  placeHolder={'Search cars...'}
                  handleSearch={handleSearch}
                  value={search}
                />
              </Box>

              <Box
                component='img'
                src={'/static/inventory_page/filter.svg'}
                alt='Filter'
                sx={{
                  width: 55,
                  height: 55,
                  display: 'block',
                  mx: 'auto',
                  cursor: 'pointer',
                }}
                onClick={openFilterDrawer}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            backgroundColor: 'black',
            py: { xs: 1, sm: 2 },
            px: 5,
            display: 'flex',
            alignItems: 'center',
            mt: 2,
          }}
        >
          <Grid
            size={{ xs: 12 }}
            mt={showCarType ? 2 : 0}
          >
            <Grid
              container
              spacing={2}
            >
              {carType.map((car, index) => (
                <Grid
                  size={{ xs: 6, md: 3 }}
                  key={index}
                >
                  <Card
                    onClick={() => handleCarTypeClick(car.code)}
                    sx={{
                      borderRadius: '12px',
                      p: car.code === selectedBodyType ? '3px' : '0',
                      border:
                        car.code === selectedBodyType
                          ? 'none'
                          : '1px solid #DBDBDB',
                      background:
                        car.code === selectedBodyType
                          ? '#be9e61'
                          : 'transparent',
                      transition: 'all 0.3s ease',
                      boxShadow: 'none',
                      textAlign: 'center',
                      maxHeight: '150px',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: '10px',
                        background: '#fff',
                        height: '100%',
                        width: '100%',
                        p: 1,
                      }}
                    >
                      <Box
                        component='img'
                        src={car.image}
                        alt={car.name}
                        sx={{
                          width: '100%',
                          maxWidth: { xs: '200px', md: '250px' },
                          height: 'auto',
                          objectFit: 'contain',
                          mx: 'auto',
                          mb: 0.5,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#000',
                          lineHeight: '12px',
                        }}
                      >
                        {car.name}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {(hasRecordFound || isLoading) && (
          <Grid
            container
            sx={{
              backgroundColor: 'black',
              py: 2,
              px: 5,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  bgcolor: 'white',
                  width: '100%',
                  display: 'flex',
                  // minHeight: '75vh',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  borderRadius: '16px',
                }}
              >
                <Loader />
              </Box>
            )}
            {!isLoading && data.length === 0 && hasRecordFound && (
              <Box
                sx={{
                  bgcolor: 'white',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 2,
                  p: 5,
                  borderRadius: '16px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#000',
                  }}
                >
                  {'No Record'}
                </Typography>
              </Box>
            )}

            <Grid
              container
              spacing={2}
              sx={{ width: '100%' }}
            >
              {data.map(item => {
                const { symbolOrCode, amount } = parseCurrencyString(
                  item?.price
                );
                return (
                  <Grid
                    key={item.id}
                    size={{ xs: 12, sm: 6, md: 4 }}
                  // ref={
                  //   idx + 1 === data?.length ? lastElementRef : undefined
                  // }
                  >
                    <Card
                      sx={{
                        borderRadius: '16px',
                        padding: '12px',
                        backgroundColor: '#FFFFFF',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        boxShadow: '0 0 15px 0px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          cursor: 'pointer',
                        },
                      }}
                      onClick={() => {
                        navigate(`/info/${encryptString(item.id)}`);
                        // window.open(item?.link, '_blank');
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '200px',
                          borderRadius: '6px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          component='img'
                          src={
                            item?.photos[0]?.key
                              ? getAssetURL(
                                item?.photos[0]?.key,
                                item?.photos[0]?.url
                              )
                              : item?.photos[0]?.url
                                ? item?.photos[0]?.url
                                : '/static/inventory_page/car_placeholder.png'
                          }
                          alt={item?.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            cursor: 'pointer',
                            opacity:
                              item?.availability !== CarAvailability.available
                                ? 0.5
                                : 1,
                          }}
                        />

                        {item?.availability !== CarAvailability.available && (
                          <Box
                            component='img'
                            src='/static/inventory_page/sold_out.png'
                            alt='Sold Out'
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              maxWidth: '80%',
                              maxHeight: '80%',
                              pointerEvents: 'none',
                              userSelect: 'none',
                            }}
                          />
                        )}
                      </Box>

                      <Divider sx={{ my: 1, mt: 1 }} />

                      {/* Car Name & Badge */}
                      <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        {/* Left box: MRP and Price stacked vertically */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#000',
                            }}
                          >
                            {item?.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: 'grey.600',
                              fontWeight: 500,
                              mt: '4px',
                            }}
                          >
                            {item?.dealerName}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 500,
                              color: 'grey.600',
                            }}
                          >
                            {'MRP'}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '18px',
                              fontWeight: 600,
                              background: (theme: any) =>
                                theme.palette.gradients.primary,
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              color: 'transparent',
                              mt: 0,
                            }}
                          >
                            {symbolOrCode}
                            {amount}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}
        <Box
          component={'img'}
          src='/static/home_page/img_collage.png'
          sx={{
            width: '100%',
          }}
        />

        <Drawer
          anchor='right'
          open={toggleDrawerState.filter}
          onClose={() => toggleDrawer('filter', false)}
        >
          <InventoryFilterDrawer
            toggleDrawer={toggleDrawer}
            keyName='filter'
            handleResetFilters={handleResetFilters}
            setCommonFilter={setCommonFilter}
            commonFilter={commonFilter}
            isFilterApplied={isFilterApplied}
            applyFilter={applyFilter}
          />
        </Drawer>
      </Page>
    </>
  );
};

export default Inventory;
